import type { Segment, Style, TermLine } from '@dreamquiz/shared';

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const STYLE_CLASS: Record<Style, string> = {
  default: '',
  muted: 's-muted',
  accent: 's-accent',
  ok: 's-ok',
  error: 's-error',
  warn: 's-warn',
  gold: 's-gold',
  highlight: 's-highlight',
};

/**
 * Die Terminalausgabe im Browser.
 *
 * Wichtig: Serverzeilen werden ausschließlich per textContent gesetzt.
 * Damit kann fremder Text (Spielernamen, Antworten) nichts einschleusen -
 * die zweite Hälfte des Schutzes aus Konzept Kap. 10, die erste ist der
 * Steuerzeichen-Filter auf dem Server.
 */
export class TerminalView {
  private readonly lineEls = new Map<string, HTMLElement>();
  private typewriterTimer: number | null = null;

  constructor(
    private readonly out: HTMLElement,
    private readonly input: HTMLInputElement,
    container: HTMLElement,
  ) {
    container.addEventListener('click', () => {
      if (window.getSelection()?.toString()) return;
      this.focus();
    });
  }

  focus(): void {
    if (!this.input.disabled) this.input.focus({ preventScroll: true });
  }

  clear(): void {
    this.out.replaceChildren();
    this.lineEls.clear();
  }

  print(lines: TermLine[]): void {
    for (const line of lines) this.appendLine(line);
    this.scrollToBottom();
  }

  /** Ersetzt eine Zeile mit derselben id - für Countdowns in derselben Zeile. */
  replace(id: string, line: TermLine): void {
    const existing = this.lineEls.get(id);
    if (!existing) {
      this.appendLine({ ...line, id });
      this.scrollToBottom();
      return;
    }
    this.fill(existing, line.segs);
  }

  /** Zeile, die nur der Client erzeugt (Boot-Animation). */
  local(text: string, style: Style = 'default'): HTMLElement {
    const el = this.appendLine({ segs: [{ t: text, s: style }] });
    this.scrollToBottom();
    return el;
  }

  setEnabled(enabled: boolean): void {
    this.input.disabled = !enabled;
    if (enabled) this.focus();
  }

  setMasked(masked: boolean): void {
    this.input.classList.toggle('masked', masked);
    this.input.type = masked ? 'password' : 'text';
  }

  private appendLine(line: TermLine): HTMLElement {
    const el = document.createElement('div');
    if (line.id) {
      this.lineEls.get(line.id)?.remove();
      this.lineEls.set(line.id, el);
    }
    this.out.appendChild(el);

    if (line.typewriter && !REDUCED_MOTION) {
      this.typewrite(el, line.segs);
    } else {
      this.fill(el, line.segs);
    }
    return el;
  }

  private fill(el: HTMLElement, segments: Segment[]): void {
    el.replaceChildren();
    for (const segment of segments) {
      const span = document.createElement('span');
      const cls = STYLE_CLASS[segment.s ?? 'default'];
      if (cls) span.className = cls;
      span.textContent = segment.t; // niemals innerHTML
      el.appendChild(span);
    }
  }

  /** Tipp-Effekt für KI-Ausgaben (Konzept Kap. 7). */
  private typewrite(el: HTMLElement, segments: Segment[]): void {
    const full = segments.map((s) => s.t).join('');
    let shown = 0;
    this.fill(el, []);
    const spans = segments.map((segment) => {
      const span = document.createElement('span');
      const cls = STYLE_CLASS[segment.s ?? 'default'];
      if (cls) span.className = cls;
      el.appendChild(span);
      return span;
    });

    const step = () => {
      shown += 2;
      let rest = shown;
      segments.forEach((segment, i) => {
        const take = Math.max(0, Math.min(segment.t.length, rest));
        spans[i]!.textContent = segment.t.slice(0, take);
        rest -= segment.t.length;
      });
      this.scrollToBottom();
      if (shown < full.length) {
        this.typewriterTimer = window.setTimeout(step, 14);
      }
    };
    step();
  }

  private scrollToBottom(): void {
    this.out.scrollTop = this.out.scrollHeight;
  }

  dispose(): void {
    if (this.typewriterTimer !== null) window.clearTimeout(this.typewriterTimer);
  }
}
